// screens/staff/SegreteriaCodiciScreen.tsx
//
// W6 v1 Session 1 — Segreteria invite-code minting + management.

import { useEffect, useRef, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import { staffApi, type InviteCode, type Cohort } from '../../api/staff';

interface NewCodeFormState {
  cohort_id:  string;
  max_uses:   number;
  expires_at: string;
  notes:      string;
}

const EMPTY_FORM: NewCodeFormState = {
  cohort_id:  '',
  max_uses:   1,
  expires_at: '',
  notes:      '',
};

export default function SegreteriaCodiciScreen(): JSX.Element {
  const tokens = useTokens();
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<NewCodeFormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState<InviteCode | null>(null);
  const [copyState, setCopyState] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<InviteCode | null>(null);
  const [revoking, setRevoking] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [c, coh] = await Promise.all([staffApi.listInviteCodes(), staffApi.listCohorts()]);
      setCodes(c.invite_codes);
      setCohorts(coh.cohorts);
    } catch {
      setError('Impossibile caricare i codici di invito.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await staffApi.createInviteCode({
        purpose:    'student',
        cohort_id:  createForm.cohort_id || null,
        max_uses:   createForm.max_uses,
        expires_at: createForm.expires_at ? new Date(createForm.expires_at).toISOString() : null,
        notes:      createForm.notes.trim() || null,
      });
      setCreatedCode(res.invite_code);
      await refresh();
    } catch {
      setError('Impossibile creare il codice di invito.');
    } finally {
      setCreating(false);
    }
  }

  function closeCreate() {
    setShowCreate(false);
    setCreateForm(EMPTY_FORM);
    setCreatedCode(null);
  }

  async function handleRevoke() {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await staffApi.revokeInviteCode(confirmRevoke.code);
      await refresh();
    } catch {
      setError('Impossibile revocare il codice.');
    } finally {
      setRevoking(false);
      setConfirmRevoke(null);
    }
  }

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopyState(code);
      setTimeout(() => setCopyState((cur) => (cur === code ? null : cur)), 2000);
    } catch { /* ignore */ }
  }

  return (
    <section data-testid="segreteria-codici-screen">
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>Codici di invito</h1>
          <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
            Generi un codice e lo consegni allo studente. Ogni codice è monouso salvo diversa indicazione.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="segreteria-new-code"
          style={{
            background:   tokens.accent,
            color:        tokens.mode === 'dark' ? '#1a1510' : '#fff',
            border:       'none',
            borderRadius: tokens.radius.pill,
            padding:      '10px 20px',
            cursor:       'pointer',
            fontSize:     14,
            fontWeight:   600,
          }}
        >
          + Crea nuovo codice
        </button>
      </header>

      {error && <div role="alert" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div data-testid="segreteria-codes-loading" style={{ color: tokens.textMuted, fontSize: 14 }}>Caricamento…</div>
      ) : codes.length === 0 ? (
        <div data-testid="segreteria-codes-empty" style={{ color: tokens.textMuted, fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
          Non sono ancora stati generati codici. Ne crei uno per iniziare.
        </div>
      ) : (
        <div role="table" aria-label="Codici di invito" data-testid="segreteria-codes-table" style={{ width: '100%', border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.6fr 1fr 1fr 0.8fr', padding: '10px 14px', background: tokens.surface, fontSize: 12, color: tokens.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <span>Codice</span>
            <span>Classe</span>
            <span>Usi</span>
            <span>Scadenza</span>
            <span>Note</span>
            <span style={{ textAlign: 'right' }}>Azioni</span>
          </div>
          {codes.map((c) => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            return (
              <div key={c.code} role="row" data-testid={`segreteria-code-row-${c.code}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.6fr 1fr 1fr 0.8fr', padding: '12px 14px', borderTop: `1px solid ${tokens.line}`, fontSize: 14, alignItems: 'center', opacity: expired ? 0.5 : 1 }}>
                <span style={{ fontFamily: tokens.fonts.mono, fontSize: 13, letterSpacing: 0.5 }}>
                  {c.code}
                  <button
                    aria-label={`Copia codice ${c.code}`}
                    onClick={() => copy(c.code)}
                    style={{ marginLeft: 8, background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.sm, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                  >
                    {copyState === c.code ? '✓' : 'Copia'}
                  </button>
                </span>
                <span style={{ color: tokens.textMuted, fontSize: 13 }}>{c.cohort_name ?? '—'}</span>
                <span style={{ color: tokens.textMuted, fontSize: 13 }}>{c.use_count}/{c.max_uses}</span>
                <span style={{ color: tokens.textMuted, fontSize: 13 }}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString('it-IT') : 'Mai'}</span>
                <span style={{ color: tokens.textMuted, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes ?? '—'}</span>
                <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setConfirmRevoke(c)}
                    data-testid={`segreteria-revoke-${c.code}`}
                    aria-label={`Revoca codice ${c.code}`}
                    disabled={!!expired}
                    style={{ background: 'transparent', color: tokens.wrong, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.sm, padding: '6px 10px', fontSize: 12, cursor: expired ? 'not-allowed' : 'pointer' }}
                  >Revoca</button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <ModalShell title={createdCode ? 'Codice creato' : 'Nuovo codice di invito'} onClose={closeCreate} testid="segreteria-new-code-modal">
          {createdCode ? (
            <div data-testid="segreteria-new-code-result">
              <p style={{ marginBottom: 12, color: tokens.textMuted, fontSize: 14 }}>Comunichi questo codice allo studente:</p>
              <div style={{ background: tokens.surface, padding: '14px 18px', borderRadius: tokens.radius.md, fontFamily: tokens.fonts.mono, fontSize: 22, letterSpacing: 1, marginBottom: 16, textAlign: 'center' }}>
                {createdCode.code}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => copy(createdCode.code)} style={btnSecondary(tokens)}>Copia</button>
                <button onClick={closeCreate} style={btnPrimary(tokens)}>Fatto</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FieldLabel tokens={tokens} label="Classe (opzionale)">
                <select value={createForm.cohort_id} onChange={(e) => setCreateForm({ ...createForm, cohort_id: e.target.value })} style={inputStyle(tokens)} data-testid="segreteria-new-code-cohort">
                  <option value="">Nessuna classe</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Numero di utilizzi" hint="1 = monouso (uno studente). 100 = codice di classe (più studenti).">
                <input type="number" min={1} max={500} value={createForm.max_uses} onChange={(e) => setCreateForm({ ...createForm, max_uses: parseInt(e.target.value, 10) || 1 })} style={inputStyle(tokens)} data-testid="segreteria-new-code-max-uses" />
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Scadenza (opzionale)">
                <input type="date" value={createForm.expires_at} onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })} style={inputStyle(tokens)} />
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Note interne (opzionale)">
                <textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} maxLength={500} rows={2} style={{ ...inputStyle(tokens), resize: 'vertical' }} data-testid="segreteria-new-code-notes" />
              </FieldLabel>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={closeCreate} style={btnSecondary(tokens)}>Annulla</button>
                <button type="submit" disabled={creating} style={btnPrimary(tokens)} data-testid="segreteria-new-code-submit">
                  {creating ? 'Generazione…' : 'Crea codice'}
                </button>
              </div>
            </form>
          )}
        </ModalShell>
      )}

      {confirmRevoke && (
        <ModalShell title="Conferma revoca codice" onClose={() => setConfirmRevoke(null)} testid="segreteria-revoke-modal">
          <p style={{ marginBottom: 16, fontSize: 14 }}>
            Confermare la revoca del codice <strong style={{ fontFamily: tokens.fonts.mono }}>{confirmRevoke.code}</strong>? Una volta revocato non sarà più utilizzabile.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmRevoke(null)} style={btnSecondary(tokens)}>Annulla</button>
            <button onClick={handleRevoke} disabled={revoking} style={btnPrimary(tokens)} data-testid="segreteria-revoke-confirm">
              {revoking ? 'Revoca in corso…' : 'Revoca'}
            </button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Local style helpers (intentionally local — keeping this screen self-contained)
// ---------------------------------------------------------------------------

function inputStyle(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '10px 14px', color: tokens.text, fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: tokens.fonts.body, outline: 'none' };
}
function btnPrimary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: tokens.accent, color: tokens.mode === 'dark' ? '#1a1510' : '#fff', border: 'none', borderRadius: tokens.radius.pill, padding: '10px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
}
function btnSecondary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '10px 18px', cursor: 'pointer', fontSize: 14 };
}

function FieldLabel({ tokens, label, hint, children }: { tokens: ReturnType<typeof useTokens>; label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: tokens.textMuted }}>
      <span>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: tokens.textFaint, marginTop: 2 }}>{hint}</span>}
    </label>
  );
}

function ModalShell({ title, onClose, children, testid }: { title: string; onClose: () => void; children: React.ReactNode; testid?: string }): JSX.Element {
  const tokens = useTokens();
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    const node = containerRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (first ?? node).focus();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div data-testid={testid} role="dialog" aria-modal="true" aria-label={title}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* TODO v1.1: add Tab focus-trap so keyboard focus can't leak to underlying page. */}
      <div ref={containerRef} tabIndex={-1} style={{ background: tokens.bg, borderRadius: tokens.radius.lg, padding: 24, minWidth: 420, maxWidth: 520, border: `1px solid ${tokens.line}`, outline: 'none' }}>
        <h2 style={{ fontFamily: tokens.fonts.serif, fontSize: 20, fontWeight: 700, margin: '0 0 16px 0' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
